import { IconButton, Menu, MenuItem, Stack, Typography } from "@mui/material";
import EmailIcon from "src/assets/svg-icons/email.svg";
import CloseIcon from "src/assets/svg-icons/close_trans.svg";
import WhatsAppIcon from "src/assets/svg-icons/whatsapp.svg";
import CalcIcon from "src/assets/svg-icons/logo.svg";
import { useEffect, useState } from "react";
import { useRecoilState } from "recoil";
import { notificationsState } from "src/store/notifications";
import DownloadIcon from "src/assets/svg-icons/download.svg";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ConfirmPopup } from "src/shared/components/confirm-popup";
import { specificBranchHasPermission } from "src/utils";
import { InventoryPermissions } from "src/constants/permissions";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  useCancelPoMutation,
  useSendPoMutation,
} from "src/api/inventory/reorder";
import { CancelPurchaseOrderPopup } from "../confirm-cancel-sent";

interface IAction {
  row: any;
  refetch: Function;
}

const TableActions = (props: IAction) => {
  const { row, refetch } = props;
  const { id, is_canceled, send_method, branch } = row;
  const { t } = useTranslation("inventory");
  const { t: generalT } = useTranslation("general");
  const navigate = useNavigate();
  const [notifications, setNotifications] = useRecoilState(notificationsState);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [actionType, setActionType] = useState<string>();

  // APIS
  const { mutate, isPending, status, error } = useCancelPoMutation();
  const {
    mutate: mutateSend,
    error: errorSend,
    status: statusSend,
    isPending: isPendingSend,
  } = useSendPoMutation();

  //permissions
  const viewPoPermission = specificBranchHasPermission(
    InventoryPermissions.viewPoReq,
    branch?.id,
  );
  const sendPoPermission = specificBranchHasPermission(
    InventoryPermissions.sendPoReq,
    branch?.id,
  );
  const cancelPoPermission = specificBranchHasPermission(
    InventoryPermissions.cancelPoReq,
    branch?.id,
  );

  const handleOpenConfirm = (type: string) => {
    setActionType(type);
    setOpenConfirm(true);
  };

  const handleCloseConfirm = () => setOpenConfirm(false);

  const handleClick = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const navigateToPoDetails = () => {
    navigate(`/inventory/reorder/po/${id}`);
  };

  const handleConfirm = () => {
    if (actionType === "cancel") {
      mutate({ id });
    } else if (actionType === "sendApp") {
      mutateSend({
        id,
        send_method: send_method === "app" ? "not_sent" : "app",
      });
    } else if (actionType === "sendEmail") {
      mutateSend({
        id,
        send_method: send_method === "email" ? "not_sent" : "email",
      });
    }
  };

  useEffect(() => {
    if (status === "success") {
      refetch();
      handleCloseConfirm();
      setNotifications([
        ...notifications,
        {
          type: "success",
          message: t("cancelled"),
        },
      ]);
    } else if (status === "error") {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: error?.data.errors[0].value || t("somethingWrong"),
        },
      ]);
    }
  }, [status]);

  useEffect(() => {
    if (statusSend === "success") {
      refetch();
      handleCloseConfirm();
      setNotifications([
        ...notifications,
        {
          type: "success",
          message: generalT("done"),
        },
      ]);
    } else if (statusSend === "error") {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: errorSend?.data.errors[0].value || t("somethingWrong"),
        },
      ]);
    }
  }, [statusSend]);

  return (
    <Stack direction={"row"} spacing={"12px"} justifyContent={"center"}>
      <IconButton size="small" onClick={(e) => handleClick(e)}>
        <MoreVertIcon />
      </IconButton>

      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "basic-button",
        }}
      >
        {sendPoPermission && (
          <MenuItem
            onClick={(e) => handleOpenConfirm("sendEmail")}
            sx={{ gap: 1 }}
            disabled={is_canceled}
          >
            <img width={17} src={EmailIcon} alt="" />
            <Typography
              color={"#1D2939"}
              fontSize={"16px"}
              textTransform={"capitalize"}
            >
              {t("sendEmail")}
            </Typography>
          </MenuItem>
        )}
        {sendPoPermission && (
          <MenuItem
            onClick={(e) => handleOpenConfirm("sendApp")}
            sx={{ gap: 1 }}
            disabled={is_canceled}
          >
            <img width={16} src={CalcIcon} alt="" />
            <Typography
              color={"#1D2939"}
              fontSize={"16px"}
              textTransform={"capitalize"}
            >
              {t("sendApp")}
            </Typography>
          </MenuItem>
        )}

        {cancelPoPermission && !is_canceled && (
          <MenuItem
            sx={{ gap: 1 }}
            onClick={(e) => handleOpenConfirm("cancel")}
          >
            <img width={17} src={CloseIcon} alt="" />
            <Typography
              color={"#1D2939"}
              fontSize={"16px"}
              textTransform={"capitalize"}
            >
              {t("cancel")}
            </Typography>
          </MenuItem>
        )}

        <MenuItem sx={{ gap: 1 }} disabled={is_canceled}>
          <img width={15} src={WhatsAppIcon} alt="" />
          <Typography
            color={"#1D2939"}
            fontSize={"16px"}
            textTransform={"capitalize"}
          >
            {t("shareWhatsApp")}
          </Typography>
        </MenuItem>

        {viewPoPermission && (
          <MenuItem onClick={navigateToPoDetails} sx={{ gap: 1 }}>
            <VisibilityOutlinedIcon sx={{ fontSize: "16px" }} />
            <Typography
              color={"#1D2939"}
              fontSize={"16px"}
              textTransform={"capitalize"}
            >
              {t("details")}
            </Typography>
          </MenuItem>
        )}
      </Menu>

      {/* confirm popups */}
      {actionType === "cancel" && row.sent_at ? (
        <CancelPurchaseOrderPopup
          open={openConfirm}
          handleClose={handleCloseConfirm}
          handleConfirm={handleConfirm}
          isPending={isPending}
        />
      ) : (
        <ConfirmPopup
          open={openConfirm}
          item=""
          handleClose={handleCloseConfirm}
          handleConfirm={handleConfirm}
          isPending={isPending || isPendingSend}
          title={
            actionType === "sendApp"
              ? t("sendApp")
              : actionType === "sendEmail"
                ? t("sendEmail")
                : generalT("cancel")
          }
          subtitle={generalT("areYouSureToConfirm")}
          confirmBtnText={generalT("confirm")}
          noIcon
        />
      )}
    </Stack>
  );
};
export default TableActions;
